import clsx from "clsx";
import { Col, ModalProps, Typography, useToast } from "@commonlibs";
import { createModal } from "@commonlibs";
import Modal from "ui/common/components/feedback/Modal/Modal";
import ModalFooter from "ui/common/components/feedback/Modal/ModalFooter/ModalFooter";
import useTranslate from "ui/locale/hooks/useTranslate";
import FileUploader from "ui/partner/container/FileUploader/FileUploader";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { Queries } from "ui/query/queries";
import useGetPartnerGiftCardStrategies from "features/giftcards/hooks/useGetPartnerGiftCardStrategies";
import { ModalGiftCardsSelect } from "../common/GiftCardsModal.styles";
import useLoadGiftCardFileStock from "features/giftcards/hooks/useLoadGiftCardFileStock";
import DomainError from "domain/error/DomainError";
import DomainErrorCodes from "domain/error/DomainErrorCodes";
import useVerifyReleaseStrategyMethod from "ui/release-strategy/hooks/useVerifyReleaseStrategyMethod";
import { Operations } from "ui/admins/operations.types";

export const LoadGiftCardStockModal = createModal<ModalProps>(({ className, close, ...rest }) => {
    const translate = useTranslate();
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const translateError = useTranslate("error");
    const [partnerStrategies, setPartnerStrategies] = useState<{ label: string; value: string }[]>([]);
    const [uploadedFile, setUploadedFile] = useState<File>();
    const [selectedPartnerAddress, setSelectedPartner] = useState<string>();
    const { data: partnerGiftCardStrategies } = useGetPartnerGiftCardStrategies();

    const { mutate, isLoading } = useLoadGiftCardFileStock({ onSuccess: handleOnSuccess, onError: handleOnError });

    const { verifyReleaseMethod, isValidatingReleaseStrategy } = useVerifyReleaseStrategyMethod({
        onSuccess: handleOnSuccess,
        onError: handleOnError,
        isLoading,
    });

    useEffect(() => {
        queryClient.invalidateQueries(Queries.PARTNER_FIND_ALL_WITHOUT_PARENT);
    }, [queryClient]);

    useEffect(() => {
        if (partnerGiftCardStrategies) {
            setPartnerStrategies(partnerGiftCardStrategies.map((partner) => ({ label: partner.name, value: partner.address })));
        }
    }, [partnerGiftCardStrategies]);

    function handleOnLoadDoc(file: File) {
        setUploadedFile(file);
    }

    function handleOnSuccess() {
        showToast(translate("giftCardsLoadedSuccessfully"), { type: "success" });
        close();
    }

    function handleOnError(error: unknown) {
        if (error instanceof DomainError && error.message === DomainErrorCodes.LOCAL_GIFTCARD_INVALID_TYPE) {
            showToast(translateError("localGiftcardInvalidType"), { type: "warning" });
        } else if (error instanceof DomainError && error.message === DomainErrorCodes.LOCAL_GIFTCARD_NOT_FOUND) {
            showToast(translateError("localGiftcardNotFound"), { type: "warning" });
        } else {
            showToast(translateError("somethingWentWrong"), { type: "error" });
        }
    }

    const isValidForm = () => {
        return Boolean(uploadedFile) && Boolean(selectedPartnerAddress);
    };

    function handleOnSubmit() {
        if (!isValidForm()) return;
        const formData = buildFormData();

        const executeAction = (...args: unknown[]) => {
            const [payload] = args as [{ payload: FormData }];
            mutate(payload);
        };

        verifyReleaseMethod({
            operationTypeId: Operations.UPLOAD_GIFTCARD_STOCK,
            originalFn: executeAction,
            originalArgs: [{ payload: formData }],
            file: uploadedFile,
        });
    }

    function buildFormData() {
        const formData = new FormData();
        const fields = {
            partner: selectedPartnerAddress,
        };

        Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, String(value));
        });

        if (uploadedFile) {
            formData.append("file", uploadedFile);
        }
        return formData;
    }

    return (
        <Modal
            title={translate("loadingVouchersStock")}
            className={clsx("load-giftcards-modal", className)}
            style={{ width: "550px" }}
            {...rest}
        >
            <Col gap={20}>
                <ModalGiftCardsSelect
                    value={selectedPartnerAddress}
                    placeholder={translate("choosePartnerStrategy")}
                    label={translate("partner")}
                    onChange={(e) => setSelectedPartner(e)}
                    options={partnerStrategies}
                />
                <Col gap={10}>
                    <Typography variant="body3Strong">{translate("giftcardFileUploaderExplanation")}</Typography>
                    <FileUploader
                        onDeleteDocument={() => setUploadedFile(undefined)} // skipcq: JS-W1042
                        onLoadDocument={handleOnLoadDoc}
                        showExplanationSection={false}
                        fileTypes=".xlsx,.xls,.csv,.xlsm"
                    />
                </Col>
                <ModalFooter
                    isLoading={isLoading || isValidatingReleaseStrategy}
                    cancelLabel={translate("cancel")}
                    mainLabel={translate("send")}
                    onCancel={close}
                    onSubmit={handleOnSubmit}
                    disabled={!isValidForm()}
                />
            </Col>
        </Modal>
    );
});

